<template>
	<div class="participants-wrapper">
		<div class="participants-content">
			<div v-if="isHost">
				<button
					class="mute-btn"
					@click="muteAll"
					:aria-label="$t('meetings_button_mute_everyone')"
					tabindex="0"
					@keyup.enter="muteAll"
					v-t="'meetings_button_mute_everyone'"
				/>
			</div>
			<div class="streamers-list">
				<Participant
					v-for="{ connectionId, streamId } in streamers"
					:key="connectionId"
					:connectionId="connectionId"
					:streamId="streamId"
				/>
			</div>
		</div>

		<div class="participants-content">
			<header
				v-if="watchers.length > 0"
				class="header"
				v-t="'meetings_viewerslist_observing'"
			/>
			<div class="watchers-list">
				<Participant
					v-for="connectionId in watchers"
					:key="connectionId"
					:connectionId="connectionId"
				/>
			</div>
		</div>
	</div>
</template>

<script>
import {
	getStreamingParticipants,
	getWatchingParticipants,
	getIsHostByUserId,
	getIsCoHostByUserId,
	getSession,
	postStartMuted,
	getVideoSession,
} from '../data'
import Participant, { PREEMPTIVE_MUTE_WAIT } from './Participant'
import { showAlert } from '../MeetingAlert'
import { getMyUserId, getUser } from '@/store'
import i18n from '@/i18n'

const sortByHost = (participants) => {
	return participants.slice().sort((a, b) => {
		const aIsHost = getIsHostByUserId(a.userId)
		const bIsHost = getIsHostByUserId(b.userId)
		if (aIsHost && !bIsHost) return -1
		if (!aIsHost && bIsHost) return 1
		const aIsCoHost = getIsCoHostByUserId(a.userId)
		const bIsCoHost = getIsCoHostByUserId(b.userId)
		if (aIsCoHost && !bIsCoHost) return -1
		if (!aIsCoHost && bIsCoHost) return 1
		return 0
	})
}

const sortByName = (participants) => {
	return participants.slice().sort((a, b) => {
		const aName = getUser(a.userId).fullname.toLowerCase()
		const bName = getUser(b.userId).fullname.toLowerCase()
		return aName.localeCompare(bName)
	})
}

export default {
	name: 'Participants',
	components: { Participant },

	data() {
		const id = getMyUserId()
		return {
			fakeAllMuted: false,
			shouldStartMuted: false,
			streamers: this.getStreamers(),
			watchers: this.getWatchers(),
			isHost: getIsHostByUserId(id) || getIsCoHostByUserId(id),
		}
	},

	mounted() {
		const videosession = getVideoSession()
		this.shouldStartMuted = videosession.startmuted

		this.$onEventBus({
			streamUpdate: () => {
				this.streamers = this.getStreamers()
			},
			participantUpdate: () => {
				this.streamers = this.getStreamers()
				this.watchers = this.getWatchers()
				const id = getMyUserId()
				this.isHost = getIsHostByUserId(id) || getIsCoHostByUserId(id)
			},
			videosessionUpdated: (videosession) => {
				this.shouldStartMuted = videosession.startmuted
			},
		})
	},

	methods: {
		getStreamId(participant) {
			if ('publisher' in participant) {
				return participant.publisher.streamId
			}

			if ('streams' in participant) {
				return participant.streams.camera.streamId
			}
		},

		muteAll() {
			const session = getSession()
			session.signal({ type: 'mute', data: '' }, (error) => {
				if (error) {
					console.error(error)
					showAlert({
						text: i18n.global.t('common_api_error_save'),
						error: true,
					})
				}
			})
			// preemptively show the user as muted
			this.fakeAllMuted = true
			setTimeout(() => {
				this.fakeAllMuted = false
			}, PREEMPTIVE_MUTE_WAIT)
		},
		async setStartMuted(e) {
			const shouldBeMuted = e.target.checked
			await postStartMuted(shouldBeMuted)
		},

		getStreamers() {
			return sortByHost(sortByName(getStreamingParticipants())).map((p) => ({
				streamId: this.getStreamId(p),
				connectionId: p.connection.id,
			}))
		},

		getWatchers() {
			return sortByHost(sortByName(getWatchingParticipants())).map(
				(p) => p.connection.id
			)
		},
	},
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

.participants-wrapper {
	overflow-y: scroll;
	overflow-x: hidden;
	height: 100%;
	width: 100%;
}

.header {
	background-color: #222;
	font-family: 'Avenir-Heavy';
	font-size: 14px;
	position: sticky;
	top: 0;
	padding: 8px 16px;
	color: #939496;
	display: flex;
	align-items: center;
	text-align: center;
	font-weight: normal;
	z-index: 1;
}

.header::after {
	content: '';
	flex: 1;
	border-bottom: 2px solid #2a2a2b;
}

.header::after {
	margin-left: 8px;
}

.mute-btn {
	outline: none;
	cursor: pointer;
	width: calc(100% - 32px);
	padding: 8px;
	text-align: center;
	color: var(--teal-300);
	background: #38393c;
	margin: 8px 16px;
	font-size: 12px;
	border-radius: 4px;
	&:disabled {
		cursor: default;
		color: gray;
	}
}
.mute-checkbox {
	color: gray;
	font-size: 12px;
	display: flex;
	align-items: center;
	margin: 0 16px;
	input {
		margin: 0 8px 0 0;
		appearance: none;
		width: 13px;
		height: 13px;
		border: 1px solid gray;
		outline: none;
		border-radius: 3px;
		&:checked {
			border-color: transparent;
			appearance: checkbox;
		}
	}
}
</style>
